import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';
import CaseManagementModel from '../models/CaseManagementModel';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const CaseManagementSQ = () => {
  const [caseStatus, setCaseStatus] = useState([]);
  const [recentCases, setRecentCases] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [assistanceTypes, setAssistanceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statusData, casesData, referralData, assistanceData] = await Promise.all([
          CaseManagementModel.getCaseStatus(),
          CaseManagementModel.getRecentCases(),
          CaseManagementModel.getReferralSources(),
          CaseManagementModel.getAssistanceTypes()
        ]);

        setCaseStatus(statusData);
        setRecentCases(casesData);
        setReferralSources(referralData);
        setAssistanceTypes(assistanceData);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Case Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {caseStatus.map((item, index) => (
          <div key={index} className="bg-white p-4 rounded shadow">
            <h3 className="text-sm font-medium text-gray-500">{item.status}</h3>
            <p className="text-2xl font-bold">{item.value}</p>
            <p className="text-xs text-gray-500">↑ {item.percent}% of total cases</p>
          </div>
        ))}
      </div>

      {/* Recent Case Table */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-4">Recent Cases</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Case ID', 'Age', 'Gender', 'Origin', 'Scam Type', 'Status'].map((head, i) => (
                  <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentCases.map((caseItem, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 text-sm text-gray-500">{caseItem.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{caseItem.age}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{caseItem.gender}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{caseItem.origin}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{caseItem.scam_type}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs font-semibold rounded-full leading-5 ${
                      caseItem.status === 'Assisted'
                        ? 'bg-green-100 text-green-800'
                        : caseItem.status === 'In process'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {caseItem.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referral Sources & Assistance Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Referral Sources Pie */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-base font-semibold mb-4">Referral Sources</h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={referralSources}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="source"
                  label={({ source, percent }) => `${source}: ${(percent * 100).toFixed(0)}%`}
                >
                  {referralSources.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Assistance Types Bar */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-base font-semibold mb-4">Assistance Types Provided</h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart layout="vertical" data={assistanceTypes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="type" />
                <Tooltip />
                <Bar dataKey="value" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseManagementSQ; 